import { useEffect, useState , useCallback} from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, User, BadgeCheck, MessageSquare, Pencil, Trash2, X, Check, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API } from "@/App";

const SORT_OPTIONS = [
  { value: "newest", label: "Most recent" },
  { value: "highest", label: "Highest rated" },
  { value: "lowest", label: "Lowest rated" },
];

function StarStrip({ score, size = "w-3 h-3" }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={`star-${i}`}
          className={`${size} ${
            i < score ? "fill-tbc-gold text-tbc-gold" : "text-gray-300"
          }`}
        />
      ))}
      <span className="text-xs text-gray-500 ml-1">{score}/5</span>
    </div>
  );
}

function DistributionBar({ distribution, total }) {
  if (!total) return null;
  // 1-5 stars: one row per star value, highest first.
  const buckets = [
    { label: "5", keys: ["5"] },
    { label: "4", keys: ["4"] },
    { label: "3", keys: ["3"] },
    { label: "2", keys: ["2"] },
    { label: "1", keys: ["1"] },
  ];
  return (
    <div className="space-y-1.5 mt-3" data-testid="rating-distribution">
      {buckets.map((b) => {
        const n = b.keys.reduce((s, k) => s + (distribution[k] || 0), 0);
        const pct = total ? Math.round((n / total) * 100) : 0;
        return (
          <div key={b.label} className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 w-10 text-right tabular-nums">{b.label}</span>
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full bg-tbc-gold transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-gray-500 w-8 tabular-nums">{n}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ReviewsSection({ business, currentUser, refreshKey = 0, onWriteReview }) {
  const businessId = business?.business_id;
  const isOwner = currentUser && business && currentUser.user_id === business.owner_id;

  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState("newest");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [summary, setSummary] = useState({ average: 0, total: 0, verified_count: 0, distribution: {} });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ score: 0, comment: "" });

  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");

  const fetchReviews = useCallback(async (nextSort = sort, nextVerified = verifiedOnly) => {
    if (!businessId) return;
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/businesses/${businessId}/reviews`, {
          params: { sort: nextSort, verified_only: nextVerified, limit: 50 },
        }),
        axios.get(`${API}/businesses/${businessId}/rating-summary`),
      ]);
      setReviews(listRes.data.reviews || []);
      setTotal(listRes.data.total || 0);
      setSummary(sumRes.data || {});
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("reviews fetch failed", e);
    }
  }, [businessId, sort, verifiedOnly]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const beginEdit = (r) => {
    setEditingId(r.review_id);
    setEditForm({ score: r.score, comment: r.comment || "" });
  };

  const submitEdit = async (r) => {
    if (editForm.score < 1) {
      toast.error("Score required");
      return;
    }
    try {
      await axios.put(
        `${API}/reviews/${r.review_id}`,
        { score: editForm.score, comment: editForm.comment || null },
        { withCredentials: true }
      );
      toast.success("Review updated");
      setEditingId(null);
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update review");
    }
  };

  const removeReview = async (r) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/reviews/${r.review_id}`, { withCredentials: true });
      toast.success("Review deleted");
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete review");
    }
  };

  const submitReply = async (r) => {
    if (!replyText.trim()) {
      toast.error("Reply cannot be empty");
      return;
    }
    try {
      await axios.post(
        `${API}/reviews/${r.review_id}/reply`,
        { message: replyText.trim() },
        { withCredentials: true }
      );
      toast.success("Reply posted");
      setReplyingId(null);
      setReplyText("");
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post reply");
    }
  };

  const removeReply = async (r) => {
    if (!window.confirm("Remove your reply?")) return;
    try {
      await axios.delete(`${API}/reviews/${r.review_id}/reply`, { withCredentials: true });
      toast.success("Reply removed");
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove reply");
    }
  };

  return (
    <div data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-2xl text-tbc-navy">
            Reviews <span className="text-gray-400 font-body text-base">({total})</span>
          </h2>
          {summary.total > 0 && (
            <div className="flex items-center gap-3 mt-1">
              <span className="font-heading text-xl text-tbc-navy">
                {summary.average?.toFixed?.(1) ?? summary.average}/5
              </span>
              {summary.verified_count > 0 && (
                <span className="inline-flex items-center gap-1 text-xs bg-tbc-cream text-tbc-navy px-2 py-1 rounded-full">
                  <ShieldCheck className="w-3 h-3 text-tbc-gold" />
                  {summary.verified_count} verified purchase{summary.verified_count === 1 ? "" : "s"}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const next = !verifiedOnly;
              setVerifiedOnly(next);
              fetchReviews(sort, next);
            }}
            className={`text-xs px-3 py-2 rounded-full border transition-colors ${
              verifiedOnly
                ? "bg-tbc-navy text-white border-tbc-navy"
                : "bg-white text-gray-600 border-gray-200 hover:border-tbc-navy"
            }`}
            data-testid="reviews-verified-toggle"
          >
            <ShieldCheck className="w-3 h-3 inline mr-1" />
            Verified only
          </button>
          <Select
            value={sort}
            onValueChange={(v) => {
              setSort(v);
              fetchReviews(v, verifiedOnly);
            }}
          >
            <SelectTrigger className="w-[160px] h-9" data-testid="reviews-sort-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`reviews-sort-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {summary.total > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 mb-6 max-w-md">
          <DistributionBar distribution={summary.distribution || {}} total={summary.total} />
        </div>
      )}

      {reviews.length === 0 ? (
        verifiedOnly ? (
          <div
            className="bg-gray-50 rounded-xl p-10 text-center text-gray-500"
            data-testid="reviews-empty"
          >
            No verified-purchase reviews yet.
          </div>
        ) : isOwner ? (
          <div
            className="bg-gray-50 rounded-xl p-10 text-center text-gray-500"
            data-testid="reviews-empty"
          >
            No reviews yet — share your business to start getting feedback from customers.
          </div>
        ) : (
          <div
            className="relative rounded-2xl border-2 border-dashed border-tbc-gold/50 bg-gradient-to-br from-tbc-cream/60 to-white p-8 sm:p-10 text-center overflow-hidden"
            data-testid="reviews-empty-cta"
          >
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-tbc-gold/10 blur-2xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-tbc-gold/15 text-tbc-gold mb-4">
                <Star className="w-7 h-7 fill-tbc-gold" />
              </div>
              <h3
                className="font-heading text-2xl text-tbc-navy mb-2"
                data-testid="reviews-empty-heading"
              >
                Be the first to review {business?.name || "this business"}
              </h3>
              <p className="text-gray-600 font-body text-sm max-w-md mx-auto mb-6">
                Help the community discover great Black-owned businesses. Your honest rating goes
                a long way.
              </p>
              {onWriteReview && (
                <Button
                  onClick={onWriteReview}
                  className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-7 font-medium"
                  data-testid="reviews-empty-write-review"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Write the first review
                </Button>
              )}
            </div>
          </div>
        )
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => {
            const isMine = currentUser && currentUser.user_id === review.user_id;
            const editing = editingId === review.review_id;
            const replying = replyingId === review.review_id;

            return (
              <div
                key={review.review_id}
                className="bg-gray-50 rounded-xl p-5"
                data-testid={`review-${review.review_id}`}
              >
                <div className="flex items-start gap-4">
                  {review.user_picture ? (
                    <img
                      src={review.user_picture}
                      alt={review.user_name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-tbc-navy rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-white" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-tbc-navy">{review.user_name}</p>
                        {review.verified_purchase && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-tbc-gold/20 text-tbc-navy px-1.5 py-0.5 rounded"
                            data-testid={`review-verified-${review.review_id}`}
                          >
                            <BadgeCheck className="w-3 h-3" />
                            Verified
                          </span>
                        )}
                      </div>
                      {!editing && <StarStrip score={review.score} />}
                    </div>

                    {editing ? (
                      <div className="mt-3 space-y-3">
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 5 }, (_, i) => (
                            <button
                              key={`star-${i}`}
                              type="button"
                              onClick={() => setEditForm({ ...editForm, score: i + 1 })}
                              className="p-0.5"
                              data-testid={`edit-score-${i + 1}-${review.review_id}`}
                            >
                              <Star
                                className={`w-5 h-5 ${
                                  i < editForm.score ? "fill-tbc-gold text-tbc-gold" : "text-gray-300"
                                }`}
                              />
                            </button>
                          ))}
                          <span className="ml-2 text-sm font-heading text-tbc-navy">
                            {editForm.score}/5
                          </span>
                        </div>
                        <Textarea
                          value={editForm.comment}
                          onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
                          rows={3}
                          placeholder="Update your review..."
                          data-testid={`edit-comment-${review.review_id}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => submitEdit(review)}
                            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                            data-testid={`edit-save-${review.review_id}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingId(null)}
                            data-testid={`edit-cancel-${review.review_id}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {review.comment && (
                          <p className="text-gray-600 font-body mt-2 whitespace-pre-line">
                            {review.comment}
                          </p>
                        )}
                        <div className="flex items-center justify-between flex-wrap gap-2 mt-2">
                          <p className="text-xs text-gray-400">
                            {new Date(review.created_at).toLocaleDateString()}
                            {review.updated_at && review.updated_at !== review.created_at && " · edited"}
                          </p>
                          <div className="flex items-center gap-1">
                            {isMine && (
                              <>
                                <button
                                  onClick={() => beginEdit(review)}
                                  className="text-xs text-gray-500 hover:text-tbc-navy inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white"
                                  data-testid={`review-edit-${review.review_id}`}
                                >
                                  <Pencil className="w-3 h-3" /> Edit
                                </button>
                                <button
                                  onClick={() => removeReview(review)}
                                  className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white"
                                  data-testid={`review-delete-${review.review_id}`}
                                >
                                  <Trash2 className="w-3 h-3" /> Delete
                                </button>
                              </>
                            )}
                            {isOwner && !review.owner_reply && (
                              <button
                                onClick={() => {
                                  setReplyingId(review.review_id);
                                  setReplyText("");
                                }}
                                className="text-xs text-tbc-navy hover:text-tbc-gold inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white"
                                data-testid={`review-reply-${review.review_id}`}
                              >
                                <MessageSquare className="w-3 h-3" /> Reply
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Owner reply block */}
                    {review.owner_reply && !replying && (
                      <div
                        className="mt-4 ml-2 pl-4 border-l-2 border-tbc-gold/40 bg-white rounded-r-lg p-3"
                        data-testid={`review-owner-reply-${review.review_id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-tbc-navy">
                            Response from {business?.name}
                          </span>
                          {isOwner && (
                            <button
                              onClick={() => removeReply(review)}
                              className="text-xs text-red-500 hover:text-red-700"
                              data-testid={`review-reply-delete-${review.review_id}`}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">
                          {review.owner_reply}
                        </p>
                        {review.owner_reply_at && (
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(review.owner_reply_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Owner reply form */}
                    {replying && (
                      <div className="mt-3 space-y-2" data-testid={`reply-form-${review.review_id}`}>
                        <Textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          rows={3}
                          placeholder="Thank your customer or address their feedback..."
                          data-testid={`reply-text-${review.review_id}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => submitReply(review)}
                            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                            data-testid={`reply-submit-${review.review_id}`}
                          >
                            Post reply
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setReplyingId(null);
                              setReplyText("");
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
